import { AlertCircle } from "lucide-react";
import { useId, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Los ids que un control necesita para quedar bien enlazado con su label,
 * su pista y su error. Se calculan en un solo lugar para que ninguna
 * pantalla vuelva a inventárselos.
 */
export interface FieldIds {
  /** id del control; va en `id` del input y en `htmlFor` del label. */
  controlId: string;
  /** id del texto de pista, si hay. */
  hintId: string | undefined;
  /** id del mensaje de error, si hay. */
  errorId: string | undefined;
  /** Lo que debe ir en `aria-describedby` (o `undefined` si no hay nada). */
  describedBy: string | undefined;
  /** Lo que debe ir en `aria-invalid` (`true` o `undefined`, nunca `false`). */
  invalid: true | undefined;
}

/**
 * Calcula los ids de un campo. Si no se le da `id`, genera uno estable con
 * `useId` — así dos campos "Correo" en la misma página nunca colisionan.
 *
 * `aria-describedby` apunta al ERROR cuando lo hay, y a la pista si no:
 * un lector de pantalla debe oír primero qué salió mal, no la sugerencia.
 */
export function useFieldIds(
  id: string | undefined,
  options: { hasHint?: boolean; hasError?: boolean } = {},
): FieldIds {
  const generated = useId();
  const controlId = id ?? `nh-field-${generated}`;
  const hintId = options.hasHint ? `${controlId}-hint` : undefined;
  const errorId = options.hasError ? `${controlId}-error` : undefined;

  return {
    controlId,
    hintId,
    errorId,
    describedBy: errorId ?? hintId,
    invalid: options.hasError ? true : undefined,
  };
}

export interface FieldMessageProps {
  /** Mensaje de error. Si lo hay, tapa a la pista. */
  error?: string;
  hint?: ReactNode;
  errorId?: string;
  hintId?: string;
  /**
   * Clase del texto de PISTA. El default es `text-ds-ink-3`.
   *
   * `Checkbox` la pintaba en `ds-warm-gray`, que da 3.45:1 sobre la crema
   * — por debajo del mínimo de 4.5:1 para texto pequeño. No era una
   * decisión de diseño sino una copia que se quedó atrás, así que ahora
   * los dos usan el mismo color. El prop se conserva para pistas sobre
   * otro fondo, no para volver a bajar el contraste.
   */
  hintClassName?: string;
}

/**
 * El renglón bajo un control: el error, o la pista si no hay error.
 *
 * Nunca los dos. Cuando algo falló, la pista ya no es el consejo útil; el
 * mensaje de error sí, y apilarlos obliga a leer dos frases para averiguar
 * cuál manda.
 *
 * `aria-live="polite"` en el contenedor —que existe SIEMPRE, aunque esté
 * vacío— es lo que hace que un error que aparece tras enviar se anuncie:
 * un contenedor que se monta ya con texto dentro no dispara la región
 * viva. `empty:hidden` evita que ocupe espacio mientras no hay nada.
 *
 * Vive aquí y lo usan `FormField` y `Checkbox`, que antes tenían este
 * bloque copiado. La copia importaba: son cuatro decisiones de
 * accesibilidad (el orden, el `aria-live`, los ids, el contenedor
 * permanente) y bastaba tocar una de las dos copias para que un control
 * dejara de anunciar sus errores sin que nada se viera roto.
 */
export function FieldMessage({
  error,
  hint,
  errorId,
  hintId,
  hintClassName = "text-ds-ink-3",
}: FieldMessageProps) {
  return (
    <div aria-live="polite" className="empty:hidden">
      {error ? (
        /*
         * El error no puede perderse. Antes iba en `ds-terracotta` a 11px:
         * 3.88:1 sobre la card de vidrio, por debajo del mínimo de 4.5:1,
         * y con el mismo peso visual que una pista. Ahora lleva tres
         * señales en vez de una: color legible (`ds-bad-t`, 7.15:1), un
         * grado más de tamaño, y un icono — porque quien no distingue
         * bien los rojos necesita algo que no sea el color.
         */
        <p className="flex items-start gap-1.5 text-xs font-medium text-ds-bad-t">
          <AlertCircle className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
          {/*
            El `id` va en el TEXTO, no en el párrafo. `aria-describedby`
            debe apuntar a lo que hay que leer; el icono es decorativo y
            está oculto para el lector de pantalla, así que incluirlo en la
            referencia solo añadiría un salto vacío.
          */}
          <span id={errorId}>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className={cn("text-[11px]", hintClassName)}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface FormFieldProps {
  /** Texto del label. Si se omite, no se pinta label (p. ej. un Checkbox). */
  label?: ReactNode;
  /** Acción a la derecha del label — "¿Olvidaste tu contraseña?". */
  labelAction?: ReactNode;
  /** Pista bajo el control. Se oculta cuando hay error. */
  hint?: ReactNode;
  /** Mensaje de error. Su presencia es lo que pone el campo en rojo. */
  error?: string;
  /** Marca visual y semántica de obligatorio. */
  required?: boolean;
  /** id del control que este campo envuelve (para `htmlFor`). */
  controlId?: string;
  hintId?: string;
  errorId?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Envoltura estándar de un campo: label + control + mensaje.
 *
 * El mensaje vive en un contenedor con `aria-live="polite"`: cuando el
 * error aparece tras enviar el formulario, quien usa lector de pantalla
 * se entera sin tener que volver a recorrer el formulario.
 */
export function FormField({
  label,
  labelAction,
  hint,
  error,
  required = false,
  controlId,
  hintId,
  errorId,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn("grid gap-1.5", className)}>
      {(label || labelAction) && (
        <div className="flex items-baseline justify-between gap-3">
          {label ? (
            <label
              htmlFor={controlId}
              className="text-[11px] font-medium text-ds-ink-3"
            >
              {label}
              {required && (
                <span className="ml-0.5 text-ds-terracotta" aria-hidden="true">
                  *
                </span>
              )}
            </label>
          ) : (
            <span />
          )}
          {labelAction}
        </div>
      )}

      {children}

      <FieldMessage error={error} hint={hint} errorId={errorId} hintId={hintId} />
    </div>
  );
}
